import { LinkOperation } from "namirasoft-site-react";
import { Component } from "react";
import ReactFlow, { Background, Controls, Edge, Handle, MarkerType, Node, NodeProps, Position } from "reactflow";
import "reactflow/dist/style.css";
import { InferencerTreeNode } from "./InferencerTree";

export interface InferencerDagNode
{
    key: string;
    type: string;
    entity_id: string;
    name: string;
    subtitle?: string;
    meta?: string[];
    href: string;
}

export interface InferencerDagEdge
{
    source_key: string;
    target_key: string;
    label?: string;
}

export interface InferencerDagData
{
    nodes: InferencerDagNode[];
    edges: InferencerDagEdge[];
}

export interface InferencerGraphProps
{
    tree?: InferencerTreeNode | null;
    forest?: InferencerTreeNode[] | null;
    graph?: InferencerDagData | null;
    loading?: boolean;
    error?: string | null;
    height?: number;
}

const FOREST_GAP = 80;

interface NodeStyle
{
    bg: string;
    border: string;
    text: string;
    edge: string;
}

const NODE_STYLES: Record<string, NodeStyle> =
{
    MODEL: { bg: "#ecfdf5", border: "#6ee7b7", text: "#047857", edge: "#10b981" },
    LOAD_BALANCER: { bg: "#eff6ff", border: "#93c5fd", text: "#1d4ed8", edge: "#3b82f6" },
    FAILOVER: { bg: "#fff7ed", border: "#fdba74", text: "#c2410c", edge: "#f97316" },
    RACE: { bg: "#faf5ff", border: "#d8b4fe", text: "#7e22ce", edge: "#a855f7" },
    ROUTER: { bg: "#f0fdfa", border: "#5eead4", text: "#0f766e", edge: "#14b8a6" },
    GATEWAY: { bg: "#f5f5f4", border: "#a8a29e", text: "#292524", edge: "#57534e" },
};

const DEFAULT_STYLE: NodeStyle = { bg: "#ffffff", border: "#d0d7de", text: "#24292f", edge: "#57606a" };

const TYPE_LABELS: Record<string, string> =
{
    MODEL: "Model",
    LOAD_BALANCER: "Load Balancer",
    FAILOVER: "Failover",
    RACE: "Race",
    ROUTER: "Router",
    GATEWAY: "Gateway",
};

const NODE_WIDTH = 220;
const NODE_HEIGHT = 88;
const H_GAP = 32;
const V_GAP = 60;

interface InferencerNodeData
{
    type: string;
    name: string;
    subtitle?: string;
    href: string;
}

function InferencerNode(props: NodeProps<InferencerNodeData>)
{
    let style = NODE_STYLES[props.data.type] ?? DEFAULT_STYLE;
    let onOpen = (e: React.MouseEvent) =>
    {
        e.preventDefault();
        e.stopPropagation();
        if (props.data.href)
            LinkOperation.open(props.data.href);
    };
    return (
        <div
            onClick={onOpen}
            style={{
                background: style.bg,
                border: `2px solid ${style.border}`,
                borderRadius: 8,
                padding: "8px 10px",
                width: NODE_WIDTH,
                minHeight: NODE_HEIGHT,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                cursor: props.data.href ? "pointer" : "default",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
        >
            <Handle type="target" position={Position.Top} style={{ background: style.edge }} />
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: style.text, fontWeight: 700 }}>
                {TYPE_LABELS[props.data.type] ?? props.data.type}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {props.data.name}
            </div>
            {props.data.subtitle && (
                <div style={{ fontSize: 11, color: "#4b5563", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {props.data.subtitle}
                </div>
            )}
            <Handle type="source" position={Position.Bottom} style={{ background: style.edge }} />
        </div>
    );
}

const nodeTypes = { inferencer: InferencerNode };

interface InferencerGraphState
{
    fullscreen: boolean;
}

export default class InferencerGraph extends Component<InferencerGraphProps, InferencerGraphState>
{
    override state: InferencerGraphState = { fullscreen: false };

    override componentWillUnmount(): void
    {
        if (this.state.fullscreen && typeof document !== "undefined")
            document.body.style.overflow = "";
        window.removeEventListener("keydown", this.onKeyDown);
    }

    private onKeyDown = (e: KeyboardEvent): void =>
    {
        if (e.key === "Escape" && this.state.fullscreen)
            this.setFullscreen(false);
    };

    private setFullscreen(next: boolean): void
    {
        if (next === this.state.fullscreen) return;
        if (typeof document !== "undefined")
            document.body.style.overflow = next ? "hidden" : "";
        if (next)
            window.addEventListener("keydown", this.onKeyDown);
        else
            window.removeEventListener("keydown", this.onKeyDown);
        this.setState({ fullscreen: next });
    }

    private toggleFullscreen = (): void => { this.setFullscreen(!this.state.fullscreen); };

    private computeWidth(n: InferencerTreeNode, memo: Map<string, number>): number
    {
        let cached = memo.get(n.id);
        if (cached !== undefined)
            return cached;
        let width: number;
        if (n.children.length === 0)
            width = NODE_WIDTH;
        else
        {
            let sum = 0;
            for (let c of n.children) sum += this.computeWidth(c.node, memo);
            sum += H_GAP * (n.children.length - 1);
            width = Math.max(NODE_WIDTH, sum);
        }
        memo.set(n.id, width);
        return width;
    }

    private layoutTreeAt(root: InferencerTreeNode, offsetX: number, nodes: Node[], edges: Edge[]): number
    {
        let widthMemo = new Map<string, number>();
        let totalWidth = this.computeWidth(root, widthMemo);

        let place = (n: InferencerTreeNode, x: number, y: number): void =>
        {
            let width = widthMemo.get(n.id) ?? NODE_WIDTH;
            let cx = x + width / 2;
            nodes.push({
                id: n.id,
                type: "inferencer",
                position: { x: cx - NODE_WIDTH / 2, y },
                data: { type: n.type, name: n.name, subtitle: n.subtitle, href: n.href } as InferencerNodeData,
                sourcePosition: Position.Bottom,
                targetPosition: Position.Top,
                draggable: true,
                selectable: true,
            });
            if (n.children.length === 0) return;
            let childrenTotal = 0;
            for (let c of n.children) childrenTotal += widthMemo.get(c.node.id) ?? NODE_WIDTH;
            childrenTotal += H_GAP * (n.children.length - 1);
            let childX = x + (width - childrenTotal) / 2;
            let childY = y + NODE_HEIGHT + V_GAP;
            let style = NODE_STYLES[n.type] ?? DEFAULT_STYLE;
            for (let c of n.children)
            {
                let cw = widthMemo.get(c.node.id) ?? NODE_WIDTH;
                place(c.node, childX, childY);
                edges.push({
                    id: `e-${n.id}-${c.node.id}`,
                    source: n.id,
                    target: c.node.id,
                    label: c.label,
                    type: "smoothstep",
                    animated: false,
                    style: { stroke: style.edge, strokeWidth: 2 },
                    labelStyle: { fontSize: 11, fill: style.text, fontWeight: 600 },
                    labelBgStyle: { fill: "#ffffff", fillOpacity: 0.9 },
                    labelBgPadding: [4, 2],
                    labelBgBorderRadius: 4,
                    markerEnd: { type: MarkerType.ArrowClosed, color: style.edge },
                });
                childX += cw + H_GAP;
            }
        };

        place(root, offsetX, 0);
        return totalWidth;
    }

    private layout(root: InferencerTreeNode): { nodes: Node[], edges: Edge[] }
    {
        let nodes: Node[] = [];
        let edges: Edge[] = [];
        this.layoutTreeAt(root, 0, nodes, edges);
        return { nodes, edges };
    }

    private layoutForest(roots: InferencerTreeNode[]): { nodes: Node[], edges: Edge[] }
    {
        let nodes: Node[] = [];
        let edges: Edge[] = [];
        let offsetX = 0;
        for (let root of roots)
        {
            let w = this.layoutTreeAt(root, offsetX, nodes, edges);
            offsetX += w + FOREST_GAP;
        }
        return { nodes, edges };
    }

    private layoutDag(data: InferencerDagData): { nodes: Node[], edges: Edge[] }
    {
        let inMap = new Map<string, Set<string>>();
        for (let n of data.nodes) inMap.set(n.key, new Set());
        for (let e of data.edges)
        {
            if (!inMap.has(e.target_key)) inMap.set(e.target_key, new Set());
            inMap.get(e.target_key)!.add(e.source_key);
        }

        let levelMap = new Map<string, number>();
        let inflight = new Set<string>();
        let getLevel = (key: string): number =>
        {
            let cached = levelMap.get(key);
            if (cached !== undefined)
                return cached;
            if (inflight.has(key))
                return 0;
            inflight.add(key);
            let ins = inMap.get(key);
            let level = 0;
            if (ins && ins.size > 0)
            {
                for (let src of ins) level = Math.max(level, getLevel(src) + 1);
            }
            inflight.delete(key);
            levelMap.set(key, level);
            return level;
        };
        for (let n of data.nodes) getLevel(n.key);

        let byLevel = new Map<number, InferencerDagNode[]>();
        for (let n of data.nodes)
        {
            let lvl = levelMap.get(n.key) ?? 0;
            let arr = byLevel.get(lvl);
            if (!arr) { arr = []; byLevel.set(lvl, arr); }
            arr.push(n);
        }

        let sortedLevels = Array.from(byLevel.keys()).sort((a, b) => a - b);
        let maxCount = 0;
        for (let l of sortedLevels) maxCount = Math.max(maxCount, byLevel.get(l)!.length);
        let totalWidth = Math.max(NODE_WIDTH, maxCount * (NODE_WIDTH + H_GAP));

        let styleByKey = new Map<string, NodeStyle>();
        for (let n of data.nodes)
            styleByKey.set(n.key, NODE_STYLES[n.type] ?? DEFAULT_STYLE);

        let rfNodes: Node[] = [];
        let rfEdges: Edge[] = [];

        for (let level of sortedLevels)
        {
            let arr = byLevel.get(level)!.slice().sort((a, b) => a.name.localeCompare(b.name));
            let count = arr.length;
            let step = totalWidth / (count + 1);
            let y = level * (NODE_HEIGHT + V_GAP);
            arr.forEach((n, i) =>
            {
                let x = step * (i + 1) - NODE_WIDTH / 2;
                rfNodes.push({
                    id: n.key,
                    type: "inferencer",
                    position: { x, y },
                    data: { type: n.type, name: n.name, subtitle: n.subtitle, href: n.href } as InferencerNodeData,
                    sourcePosition: Position.Bottom,
                    targetPosition: Position.Top,
                    draggable: true,
                    selectable: true,
                });
            });
        }

        for (let e of data.edges)
        {
            let style = styleByKey.get(e.source_key) ?? DEFAULT_STYLE;
            rfEdges.push({
                id: `e-${e.source_key}->${e.target_key}`,
                source: e.source_key,
                target: e.target_key,
                label: e.label,
                type: "smoothstep",
                animated: false,
                style: { stroke: style.edge, strokeWidth: 2 },
                labelStyle: { fontSize: 11, fill: style.text, fontWeight: 600 },
                labelBgStyle: { fill: "#ffffff", fillOpacity: 0.9 },
                labelBgPadding: [4, 2],
                labelBgBorderRadius: 4,
                markerEnd: { type: MarkerType.ArrowClosed, color: style.edge },
            });
        }

        return { nodes: rfNodes, edges: rfEdges };
    }

    override render()
    {
        let height = this.props.height ?? 420;
        let fullscreen = this.state.fullscreen;
        let containerStyle: React.CSSProperties = fullscreen
            ? {
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100vw",
                height: "100vh",
                border: "none",
                borderRadius: 0,
                background: "#fafbfc",
                boxSizing: "border-box",
                zIndex: 9999,
            }
            : {
                position: "relative",
                width: "100%",
                height,
                border: "1px solid #e6e8ec",
                borderRadius: 8,
                background: "#fafbfc",
                boxSizing: "border-box",
            };

        if (this.props.loading)
            return <div style={{ ...containerStyle, display: "flex", alignItems: "center", justifyContent: "center", color: "#6e7781", fontSize: 13 }}>Loading graph…</div>;
        if (this.props.error)
            return <div style={{ ...containerStyle, display: "flex", alignItems: "center", justifyContent: "center", color: "#cf222e", fontSize: 13 }}>{this.props.error}</div>;
        if (!this.props.tree && !this.props.graph && !(this.props.forest && this.props.forest.length > 0))
            return null;

        let { nodes, edges } = this.props.graph
            ? this.layoutDag(this.props.graph)
            : (this.props.forest && this.props.forest.length > 0)
                ? this.layoutForest(this.props.forest)
                : this.layout(this.props.tree!);

        return (
            <div style={containerStyle}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    fitView
                    fitViewOptions={{ padding: 0.2 }}
                    nodesConnectable={false}
                    nodesDraggable={true}
                    zoomOnScroll={true}
                    panOnDrag={true}
                    proOptions={{ hideAttribution: true }}
                >
                    <Background gap={16} size={1} color="#e6e8ec" />
                    <Controls showInteractive={false} />
                </ReactFlow>
                <button
                    type="button"
                    onClick={this.toggleFullscreen}
                    title={fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
                    style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        zIndex: 10,
                        width: 32,
                        height: 32,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        border: "1px solid #d0d7de",
                        borderRadius: 6,
                        background: "#ffffff",
                        color: "#24292f",
                        cursor: "pointer",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                        fontSize: 14,
                        lineHeight: 1,
                    }}
                >
                    {fullscreen ? "×" : "⛶"}
                </button>
            </div>
        );
    }
}